import { supabase } from '../supabase';

/**
 * Updates a user's role in both auth metadata and users table
 * @param userId The ID of the user to update
 * @param role The new role to assign ('user' or 'admin')
 * @returns A promise that resolves to a success message or rejects with an error
 */
export async function updateUserRole(userId: string, role: 'user' | 'admin'): Promise<{ success: boolean; message: string }> {
  try {
    // Call the Supabase Edge Function
    const { data, error } = await supabase.functions.invoke('update-user-role', {
      body: { userId, role },
    });

    if (error) throw error;
    
    // Also update the role in the users table
    const { error: updateError } = await supabase
      .from('users')
      .update({ role })
      .eq('id', userId);
      
    if (updateError) {
      console.warn('Failed to update role in users table:', updateError);
      throw updateError;
      // Continue anyway since the auth metadata was updated
    }
    
    return data || { success: true, message: 'ユーザーロールが更新されました' };
  } catch (error) {
    console.error('Failed to update user role:', error);
    throw new Error('ユーザーロールの更新に失敗しました');
  }
}

/**
 * Gets the current role of a user
 * @param userId The ID of the user
 * @returns The user's role or 'user' if not found
 */
export async function getUserRole(userId: string): Promise<string> {
  try {
    // First try to get from users table
    const { data: memberData, error: memberError } = await supabase
      .from('users')
      .select('role')
      .eq('id', userId)
      .single();
      
    if (!memberError && memberData?.role) {
      return memberData.role;
    }
    
    // Fall back to auth metadata
    const { data, error } = await supabase.auth.getUser();
    
    if (error || !data.user) {
      console.error('Error fetching user:', error);
      return 'user'; // Default to user role
    }
    
    return data.user.user_metadata?.role || 'user';
  } catch (error) {
    console.error('Failed to get user role:', error);
    return 'user'; // Default to user role
  }
}